/**
 * KasaKapanisPage — Kasa Doğrulama Faz 1 iskeleti (G11)
 *
 * Bu sayfa POS bazlı günlük kasa kapanış akışının ana sayfasıdır:
 *   sol panel  → tarih/lokasyon/durum filtre + kapanış listesi + "Yeni Kapanış"
 *   sağ panel  → seçili kapanış detayı (G12-G16'da sekmeler eklenecek)
 *
 * Faz 1 backend'i (PR #111) production'da. Bu skeleton sekme yer tutucularını
 * KapanisDetay altında bırakır; G12-G16 her sekmeyi tek tek bağlar.
 *
 * Anayasa Madde 1: Lokasyon dropdown'u `/api/lokasyonlar`'dan beslenir,
 * hardcoded değil. Durum filtresi (TASLAK/TAMAMLANDI) backend enum'una
 * eşlik ediyor — sabit 2 değer, master data değil.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const authHeaders = () => {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

const todayIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const containerStyle = {
    display: 'flex',
    height: 'calc(100vh - 60px)',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
};

const leftPanelStyle = {
    width: '33%',
    minWidth: 320,
    borderRight: '1px solid #ddd',
    padding: 16,
    overflowY: 'auto',
    background: '#fafafa',
};

const rightPanelStyle = {
    flex: 1,
    padding: 24,
    overflowY: 'auto',
};

const inputStyle = {
    padding: 6,
    border: '1px solid #ccc',
    borderRadius: 4,
    fontSize: 13,
    fontFamily: 'inherit',
};

const primaryButtonStyle = {
    padding: '6px 12px',
    background: '#c62828',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'inherit',
};

export default function KasaKapanisPage() {
    const [lokasyonlar, setLokasyonlar] = useState([]);
    const [kapanislar, setKapanislar] = useState([]);
    const [filtre, setFiltre] = useState({ lokasyon_id: '', baslangic: '', bitis: '', durum: '' });
    const [seciliKapanis, setSeciliKapanis] = useState(null);
    const [yeniModal, setYeniModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Lokasyonları bir kere yükle (Anayasa Madde 1 — DB'den)
    useEffect(() => {
        let aktif = true;
        fetch(`${API_URL}/api/lokasyonlar`, { headers: authHeaders() })
            .then((r) => (r.ok ? r.json() : []))
            .then((d) => {
                if (!aktif) return;
                setLokasyonlar(Array.isArray(d) ? d.filter((l) => l.aktif !== false) : []);
            })
            .catch(() => aktif && setLokasyonlar([]));
        return () => {
            aktif = false;
        };
    }, []);

    const yenileListe = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            Object.entries(filtre).forEach(([k, v]) => {
                if (v) params.set(k, v);
            });
            const url = `${API_URL}/api/kasa-kapanis${params.toString() ? `?${params}` : ''}`;
            const r = await fetch(url, { headers: authHeaders() });
            if (r.ok) {
                const data = await r.json();
                setKapanislar(Array.isArray(data) ? data : []);
            } else {
                const txt = await r.text().catch(() => '');
                setError(`Liste yüklenemedi (${r.status}) ${txt.slice(0, 200)}`);
                setKapanislar([]);
            }
        } catch (e) {
            setError(`Ağ hatası: ${e.message || e}`);
            setKapanislar([]);
        } finally {
            setLoading(false);
        }
    }, [filtre]);

    useEffect(() => {
        yenileListe();
    }, [yenileListe]);

    const lokasyonAd = (id) => {
        const l = lokasyonlar.find((x) => x.id === id);
        return l ? l.ad : `Lokasyon #${id}`;
    };

    return (
        <div style={containerStyle}>
            <aside style={leftPanelStyle}>
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    marginBottom: 16,
                }}>
                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#222' }}>
                        Kasa Kapanış
                    </h2>
                    <button
                        type="button"
                        onClick={() => setYeniModal(true)}
                        style={primaryButtonStyle}
                    >
                        + Yeni Kapanış
                    </button>
                </div>

                {/* Filtre çubuğu */}
                <div style={{
                    display: 'flex', flexDirection: 'column', gap: 8,
                    marginBottom: 16,
                }}>
                    <select
                        value={filtre.lokasyon_id}
                        onChange={(e) => setFiltre((f) => ({ ...f, lokasyon_id: e.target.value }))}
                        style={inputStyle}
                    >
                        <option value="">Tüm lokasyonlar</option>
                        {lokasyonlar.map((l) => (
                            <option key={l.id} value={l.id}>{l.ad}</option>
                        ))}
                    </select>

                    <select
                        value={filtre.durum}
                        onChange={(e) => setFiltre((f) => ({ ...f, durum: e.target.value }))}
                        style={inputStyle}
                    >
                        <option value="">Tüm durumlar</option>
                        <option value="TASLAK">Taslak</option>
                        <option value="TAMAMLANDI">Tamamlandı</option>
                    </select>

                    <div style={{ display: 'flex', gap: 6 }}>
                        <input
                            type="date"
                            value={filtre.baslangic}
                            onChange={(e) => setFiltre((f) => ({ ...f, baslangic: e.target.value }))}
                            style={{ ...inputStyle, flex: 1 }}
                            placeholder="Başlangıç"
                        />
                        <input
                            type="date"
                            value={filtre.bitis}
                            onChange={(e) => setFiltre((f) => ({ ...f, bitis: e.target.value }))}
                            style={{ ...inputStyle, flex: 1 }}
                            placeholder="Bitiş"
                        />
                    </div>
                </div>

                {/* Hata + yükleme + liste */}
                {error && (
                    <div style={{
                        color: '#c62828', fontSize: 12, marginBottom: 12,
                        padding: 8, background: '#ffebee', borderRadius: 4,
                    }}>
                        {error}
                    </div>
                )}
                {loading && (
                    <div style={{ color: '#999', fontSize: 13 }}>Yükleniyor...</div>
                )}
                {!loading && !error && kapanislar.length === 0 && (
                    <div style={{ color: '#999', fontSize: 13 }}>
                        Kapanış bulunamadı
                    </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {kapanislar.map((k) => {
                        const aktif = seciliKapanis?.id === k.id;
                        const tamamlandi = k.durum === 'TAMAMLANDI';
                        return (
                            <button
                                key={k.id}
                                type="button"
                                onClick={() => setSeciliKapanis(k)}
                                style={{
                                    padding: 10,
                                    background: aktif ? '#fdecea' : '#fff',
                                    border: `1px solid ${aktif ? '#c62828' : '#e0e0e0'}`,
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    textAlign: 'left',
                                    fontFamily: 'inherit',
                                }}
                            >
                                <span>
                                    <span style={{ fontSize: 14, fontWeight: 600, display: 'block' }}>
                                        {k.kapanis_tarihi}
                                    </span>
                                    <span style={{ fontSize: 12, color: '#666' }}>
                                        {lokasyonAd(k.lokasyon_id)}
                                    </span>
                                </span>
                                <span style={{
                                    fontSize: 11,
                                    padding: '2px 8px',
                                    borderRadius: 10,
                                    background: tamamlandi ? '#d1e7dd' : '#fff3cd',
                                    color: tamamlandi ? '#0f5132' : '#856404',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                }}>
                                    {k.durum}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </aside>

            <section style={rightPanelStyle}>
                {seciliKapanis ? (
                    <KapanisDetay
                        key={seciliKapanis.id}
                        kapanis={seciliKapanis}
                        lokasyonAd={lokasyonAd(seciliKapanis.lokasyon_id)}
                        onSaved={(guncel) => {
                            // Liste sol panelde güncellensin (durum chip vb.)
                            yenileListe();
                            if (guncel) {
                                setSeciliKapanis((prev) => (prev && prev.id === guncel.id ? { ...prev, ...guncel } : prev));
                            }
                        }}
                    />
                ) : (
                    <div style={{
                        color: '#999', textAlign: 'center', marginTop: 80, fontSize: 14,
                    }}>
                        Soldan bir kapanış seçin veya yeni oluşturun
                    </div>
                )}
            </section>

            {yeniModal && (
                <YeniKapanisModal
                    lokasyonlar={lokasyonlar}
                    onClose={() => setYeniModal(false)}
                    onCreated={(k) => {
                        setYeniModal(false);
                        yenileListe();
                        setSeciliKapanis(k);
                    }}
                />
            )}
        </div>
    );
}

// G12-G15: Nakit + Genel · Kart · Yemek Kartı · Harcamalar
// (G16 sıradaki task: Özet bar + Tamamla modal — burada henüz YOK.)
//
// Yapısal kararlar:
// - Tek bileşen 4 sekmeyi yönetir → state shared (özellikle save: tek PATCH).
// - PATCH "tam liste yer değiştirme" deseni: kart/yk/harcama listeleri her
//   save'de tüm satırlarıyla gönderilir; backend silip yeniden ekler.
// - TAMAMLANDI durumunda tüm input'lar disabled + Kaydet butonu gizli
//   (Anayasa Madde 6 — veri kaybı önleme; geri alma G16 modal'a düşecek).
// - Karşılaştırma (Modpos) verisi sadece okunur — formdaki "Modpos" kolonları
//   buradan beslenir.
// - Master data (POS terminalleri, YK sağlayıcı, Gider kategorisi) tamamı
//   API'den gelir (Anayasa Madde 1).
const TAB_LIST = [
    { kod: 'nakit', etiket: 'Nakit + Genel' },
    { kod: 'kart', etiket: 'Kart' },
    { kod: 'yk', etiket: 'Yemek Kartı' },
    { kod: 'harcama', etiket: 'Harcamalar' },
];

const tabButtonStyle = (aktif) => ({
    padding: '8px 16px',
    background: aktif ? '#c62828' : '#fff',
    color: aktif ? '#fff' : '#333',
    border: `1px solid ${aktif ? '#c62828' : '#ccc'}`,
    borderBottom: aktif ? '1px solid #c62828' : '1px solid #ccc',
    borderRadius: '4px 4px 0 0',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'inherit',
    marginRight: 4,
});

const tabBodyStyle = {
    border: '1px solid #ccc',
    borderRadius: '0 4px 4px 4px',
    padding: 20,
    background: '#fff',
    marginTop: -1,
};

const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
};

const thStyle = {
    textAlign: 'left',
    padding: '8px 10px',
    background: '#f3f3f3',
    borderBottom: '1px solid #ccc',
    fontWeight: 600,
    color: '#444',
};

const tdStyle = {
    padding: '8px 10px',
    borderBottom: '1px solid #eee',
    verticalAlign: 'middle',
};

const numInputStyle = {
    ...inputStyle,
    width: 130,
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
};

const formatTl = (n) => {
    const v = Number(n) || 0;
    return v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const farkColor = (fark) => {
    const v = Number(fark) || 0;
    if (Math.abs(v) < 0.005) return '#666';
    return v > 0 ? '#2e7d32' : '#c62828';
};

function KapanisDetay({ kapanis, lokasyonAd, onSaved }) {
    const { user } = useAuth();
    const canYenidenAc = user && hasPermission(user.role, PERMISSIONS.YENIDEN_AC_KASA_KAPANIS, user.effectivePermissions);

    const [detay, setDetay] = useState(null);
    const [karsilastirma, setKarsilastirma] = useState(null);
    const [posTerminalleri, setPosTerminalleri] = useState([]);
    const [ykSaglayicilari, setYkSaglayicilari] = useState([]);
    const [giderKategorileri, setGiderKategorileri] = useState([]);
    // Tolerans system_ayarlar'dan; backend endpoint yoksa default 5.0 TL (G5 seed değeri)
    const [tolerans, setTolerans] = useState(5.0);

    const [aktifSekme, setAktifSekme] = useState('nakit');
    const [yukleniyor, setYukleniyor] = useState(true);
    const [kaydediliyor, setKaydediliyor] = useState(false);
    const [hata, setHata] = useState(null);
    const [bildirim, setBildirim] = useState(null);

    // Tamamla + Yeniden Aç modal görünürlüğü (G16)
    const [tamamlaModal, setTamamlaModal] = useState(false);
    const [yenidenAcModal, setYenidenAcModal] = useState(false);

    // Local working copies — kullanıcı düzenleme yapabilsin
    const [beyanNakit, setBeyanNakit] = useState(0);
    const [genelNotlar, setGenelNotlar] = useState('');
    const [kartDetaylar, setKartDetaylar] = useState([]);
    const [ykDetaylar, setYkDetaylar] = useState([]);
    const [harcamalar, setHarcamalar] = useState([]);

    // Working copies değişti mi? (dirty) — Tamamla butonunu kilitlemek için.
    // Karşılaştırma working-copies init'i ile detayın server-side state'i arasındaki fark.
    const [dirty, setDirty] = useState(false);

    const tamamlandi = detay?.durum === 'TAMAMLANDI' || kapanis.durum === 'TAMAMLANDI';
    const readOnly = tamamlandi;

    // İlk yükleme — kapanış değişince hepsini paralel çek
    useEffect(() => {
        let aktif = true;
        setYukleniyor(true);
        setHata(null);
        setDetay(null);
        setKarsilastirma(null);
        setBildirim(null);

        const lid = kapanis.lokasyon_id;
        const tarih = kapanis.kapanis_tarihi;

        Promise.all([
            fetch(`${API_URL}/api/kasa-kapanis/${kapanis.id}`, { headers: authHeaders() })
                .then((r) => (r.ok ? r.json() : Promise.reject(`Kapanış detay ${r.status}`))),
            fetch(`${API_URL}/api/kasa-kapanis/karsilastirma?lokasyon_id=${lid}&tarih=${tarih}`, { headers: authHeaders() })
                .then((r) => (r.ok ? r.json() : Promise.reject(`Karşılaştırma ${r.status}`))),
            fetch(`${API_URL}/api/pos-terminalleri`, { headers: authHeaders() })
                .then((r) => (r.ok ? r.json() : [])),
            fetch(`${API_URL}/api/komisyon-oranlari?guncel=true`, { headers: authHeaders() })
                .then((r) => (r.ok ? r.json() : [])),
            fetch(`${API_URL}/api/gider-kategorileri`, { headers: authHeaders() })
                .then((r) => (r.ok ? r.json() : [])),
        ])
            .then(([d, ks, pos, kom, kat]) => {
                if (!aktif) return;
                setDetay(d);
                setKarsilastirma(ks);
                // POS terminallerini bu lokasyona filtrele
                setPosTerminalleri(
                    (Array.isArray(pos) ? pos : []).filter((t) => t.lokasyon_id === lid && t.aktif !== false)
                );
                // Komisyon listesinden YK sağlayıcılarını filtrele
                setYkSaglayicilari(
                    (Array.isArray(kom) ? kom : []).filter((k) => k.kategori === 'YEMEK_KARTI' && k.aktif !== false)
                );
                setGiderKategorileri(Array.isArray(kat) ? kat.filter((g) => g.aktif !== false) : []);
            })
            .then(() => {
                // Tolerans best-effort — backend henüz GET endpoint exposelamadı (G5 sadece seed yapar).
                // Eğer ileride /api/system/kasa-dogrulama-tolerans gibi bir endpoint eklenirse
                // burada okunur; bulunmazsa default 5.0 TL (G5 seed değeri) kullanılır.
                return fetch(`${API_URL}/api/system/kasa-dogrulama-tolerans`, { headers: authHeaders() })
                    .then((r) => (r.ok ? r.json() : null))
                    .then((tol) => {
                        if (!aktif || !tol) return;
                        const v = Number(tol.value ?? tol.tolerans ?? tol);
                        if (Number.isFinite(v) && v >= 0) setTolerans(v);
                    })
                    .catch(() => { /* sessizce default'ta kal */ });
            })
            .catch((e) => {
                if (!aktif) return;
                setHata(`Yükleme hatası: ${e?.message || e}`);
            })
            .finally(() => {
                if (aktif) setYukleniyor(false);
            });

        return () => {
            aktif = false;
        };
    }, [kapanis.id, kapanis.lokasyon_id, kapanis.kapanis_tarihi]);

    // Detay + karşılaştırma + master data geldiğinde working copy'leri kur
    useEffect(() => {
        if (!detay || !karsilastirma) return;

        setBeyanNakit(Number(detay.beyan_nakit) || 0);
        setGenelNotlar(detay.genel_notlar || '');

        // Kart detaylar: her POS terminali için bir satır; mevcut detayı koru
        const mevcutKartMap = new Map(
            (detay.kart_detaylar || []).map((d) => [d.pos_terminal_id, d])
        );
        const yeniKartlar = posTerminalleri.map((t) => {
            const mevcut = mevcutKartMap.get(t.id);
            return {
                pos_terminal_id: t.id,
                pos_terminal_ad: t.ad,
                pos_terminal_banka: t.banka,
                beyan_tutar: mevcut ? Number(mevcut.beyan_tutar) || 0 : 0,
                z_raporu_tutar: mevcut ? Number(mevcut.z_raporu_tutar) || 0 : 0,
                modpos_kart_snapshot: mevcut ? Number(mevcut.modpos_kart_snapshot) || 0 : 0,
            };
        });
        // Pos terminalı master'dan silinmiş ama detayda kalmış satırlar (legacy) →
        // gösterimde tutuyoruz, kullanıcı görsün
        (detay.kart_detaylar || []).forEach((d) => {
            if (!posTerminalleri.find((t) => t.id === d.pos_terminal_id)) {
                yeniKartlar.push({
                    pos_terminal_id: d.pos_terminal_id,
                    pos_terminal_ad: d.pos_terminal_ad || `Terminal #${d.pos_terminal_id}`,
                    pos_terminal_banka: null,
                    beyan_tutar: Number(d.beyan_tutar) || 0,
                    z_raporu_tutar: Number(d.z_raporu_tutar) || 0,
                    modpos_kart_snapshot: Number(d.modpos_kart_snapshot) || 0,
                    _orphan: true,
                });
            }
        });
        setKartDetaylar(yeniKartlar);

        // YK detayları: aktif sağlayıcılar + Modpos verisi olanlar + zaten detayda olanlar
        const ykDetay = karsilastirma.yk_detay || {};
        const mevcutYkMap = new Map(
            (detay.yk_detaylar || []).map((d) => [d.odeme_komisyon_id, d])
        );
        const yeniYk = [];
        ykSaglayicilari.forEach((s) => {
            const mevcut = mevcutYkMap.get(s.id);
            const modposTutar = Number(ykDetay[s.kod]) || 0;
            // Sadece Modpos verisi varsa VEYA önceden detayda kayıt varsa göster
            if (modposTutar > 0 || mevcut) {
                yeniYk.push({
                    odeme_komisyon_id: s.id,
                    saglayici_kod: s.kod,
                    saglayici_ad: s.ad,
                    beyan_tutar: mevcut ? Number(mevcut.beyan_tutar) || 0 : 0,
                    modpos_snapshot: modposTutar,
                });
            }
        });
        // Master'da olmayan ama eski detayda kalmış satırlar (silinmiş sağlayıcı)
        (detay.yk_detaylar || []).forEach((d) => {
            if (!ykSaglayicilari.find((s) => s.id === d.odeme_komisyon_id)) {
                yeniYk.push({
                    odeme_komisyon_id: d.odeme_komisyon_id,
                    saglayici_kod: '?',
                    saglayici_ad: `Sağlayıcı #${d.odeme_komisyon_id} (silinmiş)`,
                    beyan_tutar: Number(d.beyan_tutar) || 0,
                    modpos_snapshot: Number(d.modpos_snapshot) || 0,
                    _orphan: true,
                });
            }
        });
        setYkDetaylar(yeniYk);

        // Harcamalar — mevcut listeyi olduğu gibi al
        setHarcamalar(
            (detay.harcamalar || []).map((h) => ({
                gider_kategorisi: h.gider_kategorisi || '',
                aciklama: h.aciklama || '',
                tutar: Number(h.tutar) || 0,
            }))
        );
        // İlk init / server reload sonrası: working state = server state → dirty=false
        setDirty(false);
    }, [detay, karsilastirma, posTerminalleri, ykSaglayicilari]);

    // Bildirim 3 sn sonra söner
    useEffect(() => {
        if (!bildirim) return undefined;
        const t = setTimeout(() => setBildirim(null), 3000);
        return () => clearTimeout(t);
    }, [bildirim]);

    const yenidenYukleDetay = useCallback(async () => {
        const r = await fetch(`${API_URL}/api/kasa-kapanis/${kapanis.id}`, { headers: authHeaders() });
        if (r.ok) {
            const d = await r.json();
            setDetay(d);
        }
    }, [kapanis.id]);

    const handleKaydet = async () => {
        if (readOnly) return false;
        setKaydediliyor(true);
        setHata(null);
        try {
            const body = {
                beyan_nakit: Number(beyanNakit) || 0,
                modpos_nakit_snapshot: Number(karsilastirma?.nakit) || 0,
                genel_notlar: genelNotlar || null,
                kart_detaylar: kartDetaylar.map((d) => ({
                    pos_terminal_id: d.pos_terminal_id,
                    beyan_tutar: Number(d.beyan_tutar) || 0,
                    z_raporu_tutar: Number(d.z_raporu_tutar) || 0,
                    modpos_kart_snapshot: Number(d.modpos_kart_snapshot) || 0,
                })),
                yk_detaylar: ykDetaylar.map((d) => ({
                    odeme_komisyon_id: d.odeme_komisyon_id,
                    beyan_tutar: Number(d.beyan_tutar) || 0,
                    modpos_snapshot: Number(d.modpos_snapshot) || 0,
                })),
                harcamalar: harcamalar
                    .filter((h) => h.gider_kategorisi && Number(h.tutar) !== 0)
                    .map((h) => ({
                        gider_kategorisi: h.gider_kategorisi,
                        aciklama: h.aciklama || null,
                        tutar: Number(h.tutar) || 0,
                    })),
            };
            const r = await fetch(`${API_URL}/api/kasa-kapanis/${kapanis.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify(body),
            });
            if (r.ok) {
                await yenidenYukleDetay();
                setBildirim('Kaydedildi');
                if (onSaved) onSaved({ id: kapanis.id });
                return true;
            }
            const txt = await r.text().catch(() => '');
            setHata(`Kaydetme hatası (${r.status}) ${txt.slice(0, 240)}`);
            return false;
        } catch (e) {
            setHata(`Ağ hatası: ${e?.message || e}`);
            return false;
        } finally {
            setKaydediliyor(false);
        }
    };

    // Kullanıcı düzenleme yapınca dirty=true (Tamamla butonunu kilitler)
    const markDirty = useCallback(() => setDirty(true), []);

    // setBeyanNakit / setGenelNotlar setter'ları sekme bileşenlerine geçiriliyor;
    // dirty flag için wrapper:
    const setBeyanNakitDirty = useCallback((v) => { markDirty(); setBeyanNakit(v); }, [markDirty]);
    const setGenelNotlarDirty = useCallback((v) => { markDirty(); setGenelNotlar(v); }, [markDirty]);

    // Kart satırı güncellemeleri
    const updateKart = (idx, field, value) => {
        markDirty();
        setKartDetaylar((arr) => arr.map((d, i) => (i === idx ? { ...d, [field]: value } : d)));
    };

    // YK satırı güncellemeleri
    const updateYk = (idx, field, value) => {
        markDirty();
        setYkDetaylar((arr) => arr.map((d, i) => (i === idx ? { ...d, [field]: value } : d)));
    };

    // Harcama satırı işlemleri
    const updateHarcama = (idx, field, value) => {
        markDirty();
        setHarcamalar((arr) => arr.map((h, i) => (i === idx ? { ...h, [field]: value } : h)));
    };
    const harcamaSil = (idx) => {
        markDirty();
        setHarcamalar((arr) => arr.filter((_, i) => i !== idx));
    };
    const harcamaEkle = () => {
        markDirty();
        setHarcamalar((arr) => [...arr, { gider_kategorisi: '', aciklama: '', tutar: 0 }]);
    };

    // ─────────────────────────────────────────────────────────────────
    // G16 — Üst Özet Bar için canlı hesaplamalar
    // ─────────────────────────────────────────────────────────────────
    // Tüm rakamlar working state'ten (kullanıcı yazdığı anki değerler).
    // Fark sign convention (backend `_hesapla_toplam_fark` ile aynı):
    //   + → beyan > kaynak (eldeki para fazla)
    //   − → beyan < kaynak (eksik var)
    const ozet = useMemo(() => {
        const nakit = Number(beyanNakit) || 0;
        const kart = kartDetaylar.reduce((s, d) => s + (Number(d.beyan_tutar) || 0), 0);
        const yk = ykDetaylar.reduce((s, d) => s + (Number(d.beyan_tutar) || 0), 0);
        const harcama = harcamalar.reduce((s, h) => s + (Number(h.tutar) || 0), 0);
        const net = nakit + kart + yk - harcama;

        const nakitFark = nakit - (Number(karsilastirma?.nakit) || 0);
        const kartFark = kartDetaylar.reduce(
            (s, d) => s + ((Number(d.beyan_tutar) || 0) - (Number(d.z_raporu_tutar) || 0)),
            0
        );
        const ykFark = ykDetaylar.reduce(
            (s, d) => s + ((Number(d.beyan_tutar) || 0) - (Number(d.modpos_snapshot) || 0)),
            0
        );
        const toplamFark = nakitFark + kartFark + ykFark;
        const tolerasInde = Math.abs(toplamFark) <= tolerans;

        return { nakit, kart, yk, harcama, net, toplamFark, tolerasInde };
    }, [beyanNakit, kartDetaylar, ykDetaylar, harcamalar, karsilastirma, tolerans]);

    if (yukleniyor) {
        return (
            <div style={{ color: '#999', textAlign: 'center', marginTop: 60, fontSize: 14 }}>
                Yükleniyor...
            </div>
        );
    }

    return (
        <div style={{ position: 'relative', paddingBottom: 80 }}>
            {/* G16 — Sticky Üst Özet Bar */}
            <OzetBar ozet={ozet} tolerans={tolerans} />

            <header style={{ marginBottom: 24, marginTop: 16 }}>
                <h3 style={{
                    margin: 0, fontSize: 18, fontWeight: 700, color: '#222',
                    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                }}>
                    Kapanış #{kapanis.id}
                    <span style={{ fontSize: 14, color: '#666', fontWeight: 500 }}>
                        {kapanis.kapanis_tarihi} · {lokasyonAd}
                    </span>
                    <span style={{
                        fontSize: 11,
                        padding: '2px 8px',
                        borderRadius: 10,
                        background: tamamlandi ? '#d1e7dd' : '#fff3cd',
                        color: tamamlandi ? '#0f5132' : '#856404',
                        fontWeight: 600,
                    }}>
                        {detay?.durum || kapanis.durum}
                    </span>
                </h3>
                {tamamlandi && (
                    <div style={{
                        marginTop: 8,
                        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                    }}>
                        <div style={{
                            fontSize: 12, color: '#0f5132',
                            background: '#d1e7dd', padding: '6px 10px', borderRadius: 4,
                        }}>
                            Bu kapanış tamamlanmış — sadece okuma.
                        </div>
                        {canYenidenAc && (
                            <button
                                type="button"
                                onClick={() => setYenidenAcModal(true)}
                                style={{
                                    padding: '6px 12px',
                                    background: '#fff',
                                    color: '#666',
                                    border: '1px solid #aaa',
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    fontFamily: 'inherit',
                                }}
                                title="Yeniden Aç (TAMAMLANDI → TASLAK)"
                            >
                                Yeniden Aç
                            </button>
                        )}
                    </div>
                )}
            </header>

            {hata && (
                <div style={{
                    color: '#c62828', background: '#ffebee', padding: 10,
                    borderRadius: 4, marginBottom: 12, fontSize: 13,
                }}>
                    {hata}
                </div>
            )}

            {/* Sekme stripi */}
            <div style={{ display: 'flex', borderBottom: '1px solid #ccc', marginBottom: 0 }}>
                {TAB_LIST.map((tab) => (
                    <button
                        key={tab.kod}
                        type="button"
                        onClick={() => setAktifSekme(tab.kod)}
                        style={tabButtonStyle(aktifSekme === tab.kod)}
                    >
                        {tab.etiket}
                    </button>
                ))}
            </div>

            <div style={tabBodyStyle}>
                {aktifSekme === 'nakit' && (
                    <NakitSekme
                        beyanNakit={beyanNakit}
                        setBeyanNakit={setBeyanNakitDirty}
                        modposNakit={Number(karsilastirma?.nakit) || 0}
                        genelNotlar={genelNotlar}
                        setGenelNotlar={setGenelNotlarDirty}
                        readOnly={readOnly}
                    />
                )}
                {aktifSekme === 'kart' && (
                    <KartSekme
                        kartDetaylar={kartDetaylar}
                        updateKart={updateKart}
                        modposKart={Number(karsilastirma?.kart_toplam) || 0}
                        readOnly={readOnly}
                    />
                )}
                {aktifSekme === 'yk' && (
                    <YkSekme
                        ykDetaylar={ykDetaylar}
                        updateYk={updateYk}
                        ykSaglayicilari={ykSaglayicilari}
                        karsilastirmaYk={karsilastirma?.yk_detay || {}}
                        readOnly={readOnly}
                    />
                )}
                {aktifSekme === 'harcama' && (
                    <HarcamaSekme
                        harcamalar={harcamalar}
                        updateHarcama={updateHarcama}
                        harcamaSil={harcamaSil}
                        harcamaEkle={harcamaEkle}
                        giderKategorileri={giderKategorileri}
                        readOnly={readOnly}
                    />
                )}
            </div>

            {/* Kaydet + Tamamla butonları — sağ alt, fixed (G16) */}
            {!readOnly && (
                <div style={{
                    position: 'fixed', right: 24, bottom: 24, zIndex: 50,
                    display: 'flex', alignItems: 'center', gap: 12,
                }}>
                    {bildirim && (
                        <span style={{
                            background: '#d1e7dd', color: '#0f5132',
                            padding: '6px 12px', borderRadius: 4, fontSize: 13, fontWeight: 600,
                        }}>
                            {bildirim}
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={handleKaydet}
                        disabled={kaydediliyor}
                        style={{
                            ...primaryButtonStyle,
                            padding: '10px 24px',
                            fontSize: 14,
                            boxShadow: '0 4px 12px rgba(198,40,40,0.35)',
                            opacity: kaydediliyor ? 0.7 : 1,
                            cursor: kaydediliyor ? 'wait' : 'pointer',
                        }}
                    >
                        {kaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                    <button
                        type="button"
                        onClick={() => setTamamlaModal(true)}
                        disabled={kaydediliyor || dirty}
                        title={dirty ? 'Önce kaydet — kaydedilmemiş değişiklikler var' : 'Kasa kapanışını tamamla'}
                        style={{
                            padding: '10px 24px',
                            fontSize: 14,
                            background: dirty ? '#a5d6a7' : '#2e7d32',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            fontWeight: 700,
                            fontFamily: 'inherit',
                            boxShadow: dirty ? 'none' : '0 4px 12px rgba(46,125,50,0.35)',
                            opacity: kaydediliyor ? 0.7 : 1,
                            cursor: dirty || kaydediliyor ? 'not-allowed' : 'pointer',
                        }}
                    >
                        Tamamla
                    </button>
                </div>
            )}

            {/* G16 Modallar */}
            {tamamlaModal && (
                <TamamlaModal
                    kapanisId={kapanis.id}
                    ozet={ozet}
                    tolerans={tolerans}
                    onClose={() => setTamamlaModal(false)}
                    onCompleted={(updated) => {
                        setTamamlaModal(false);
                        setDetay(updated);
                        setBildirim('Kapanış tamamlandı');
                        if (onSaved) onSaved({ id: kapanis.id, ...updated });
                    }}
                />
            )}
            {yenidenAcModal && (
                <YenidenAcModal
                    kapanisId={kapanis.id}
                    onClose={() => setYenidenAcModal(false)}
                    onReopened={(updated) => {
                        setYenidenAcModal(false);
                        setDetay(updated);
                        setBildirim('Kapanış yeniden açıldı (TASLAK)');
                        if (onSaved) onSaved({ id: kapanis.id, ...updated });
                    }}
                />
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Sekme 1: Nakit + Genel notlar (G12)
// ─────────────────────────────────────────────────────────────────────
function NakitSekme({ beyanNakit, setBeyanNakit, modposNakit, genelNotlar, setGenelNotlar, readOnly }) {
    const fark = Number(beyanNakit || 0) - Number(modposNakit || 0);
    return (
        <div>
            <h4 style={{ margin: '0 0 16px 0', fontSize: 15, fontWeight: 700, color: '#333' }}>
                Nakit
            </h4>
            <table style={{ ...tableStyle, marginBottom: 24 }}>
                <thead>
                    <tr>
                        <th style={thStyle}>Beyan (kasada sayılan)</th>
                        <th style={thStyle}>Modpos (POS raporu)</th>
                        <th style={thStyle}>Fark</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td style={tdStyle}>
                            <input
                                type="number"
                                step="0.01"
                                value={beyanNakit}
                                onChange={(e) => setBeyanNakit(e.target.value)}
                                disabled={readOnly}
                                style={numInputStyle}
                            />
                        </td>
                        <td style={{ ...tdStyle, fontVariantNumeric: 'tabular-nums', color: '#555' }}>
                            {formatTl(modposNakit)} TL
                        </td>
                        <td style={{
                            ...tdStyle,
                            fontVariantNumeric: 'tabular-nums',
                            fontWeight: 700,
                            color: farkColor(fark),
                        }}>
                            {fark > 0 ? '+' : ''}{formatTl(fark)} TL
                        </td>
                    </tr>
                </tbody>
            </table>

            <h4 style={{ margin: '24px 0 12px 0', fontSize: 15, fontWeight: 700, color: '#333' }}>
                Genel Notlar
            </h4>
            <textarea
                value={genelNotlar}
                onChange={(e) => setGenelNotlar(e.target.value)}
                disabled={readOnly}
                rows={5}
                style={{
                    ...inputStyle,
                    width: '100%',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                    padding: 10,
                    resize: 'vertical',
                }}
                placeholder="Bu kapanışla ilgili genel notlar..."
            />
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Sekme 2: Kart (POS terminali bazında) (G13)
// ─────────────────────────────────────────────────────────────────────
function KartSekme({ kartDetaylar, updateKart, modposKart, readOnly }) {
    if (!kartDetaylar || kartDetaylar.length === 0) {
        return (
            <div style={{
                padding: 20, background: '#fff8e1', borderRadius: 4, color: '#7c5e00',
                fontSize: 13,
            }}>
                Bu lokasyon için POS terminali tanımlı değil. Ayarlar &rarr; POS Terminalleri'nden ekleyebilirsiniz.
            </div>
        );
    }

    const toplamBeyan = kartDetaylar.reduce((s, d) => s + (Number(d.beyan_tutar) || 0), 0);
    const toplamZ = kartDetaylar.reduce((s, d) => s + (Number(d.z_raporu_tutar) || 0), 0);
    const uyariBeyanZ = Math.abs(toplamBeyan - toplamZ) > 0.5;
    const uyariBeyanModpos = Math.abs(toplamBeyan - modposKart) > 5;

    return (
        <div>
            <h4 style={{ margin: '0 0 16px 0', fontSize: 15, fontWeight: 700, color: '#333' }}>
                Kart — POS Terminalleri
            </h4>
            <table style={tableStyle}>
                <thead>
                    <tr>
                        <th style={thStyle}>Terminal</th>
                        <th style={thStyle}>Banka</th>
                        <th style={thStyle}>Beyan</th>
                        <th style={thStyle}>Z Raporu</th>
                        <th style={thStyle}>Fark</th>
                    </tr>
                </thead>
                <tbody>
                    {kartDetaylar.map((d, idx) => {
                        const fark = (Number(d.beyan_tutar) || 0) - (Number(d.z_raporu_tutar) || 0);
                        return (
                            <tr key={`${d.pos_terminal_id}_${idx}`}>
                                <td style={tdStyle}>
                                    {d.pos_terminal_ad || `#${d.pos_terminal_id}`}
                                    {d._orphan && (
                                        <span style={{
                                            marginLeft: 6, fontSize: 10, color: '#856404',
                                            background: '#fff3cd', padding: '1px 6px', borderRadius: 8,
                                        }}>
                                            silinmiş
                                        </span>
                                    )}
                                </td>
                                <td style={{ ...tdStyle, color: '#666' }}>
                                    {d.pos_terminal_banka || '—'}
                                </td>
                                <td style={tdStyle}>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={d.beyan_tutar}
                                        onChange={(e) => updateKart(idx, 'beyan_tutar', e.target.value)}
                                        disabled={readOnly}
                                        style={numInputStyle}
                                    />
                                </td>
                                <td style={tdStyle}>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={d.z_raporu_tutar}
                                        onChange={(e) => updateKart(idx, 'z_raporu_tutar', e.target.value)}
                                        disabled={readOnly}
                                        style={numInputStyle}
                                    />
                                </td>
                                <td style={{
                                    ...tdStyle,
                                    fontVariantNumeric: 'tabular-nums',
                                    fontWeight: 600,
                                    color: farkColor(fark),
                                }}>
                                    {fark > 0 ? '+' : ''}{formatTl(fark)}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    <tr style={{ background: '#fafafa' }}>
                        <td style={{ ...tdStyle, fontWeight: 700 }} colSpan={2}>Toplam</td>
                        <td style={{ ...tdStyle, fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                            {formatTl(toplamBeyan)}
                        </td>
                        <td style={{ ...tdStyle, fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                            {formatTl(toplamZ)}
                        </td>
                        <td style={{ ...tdStyle }} />
                    </tr>
                </tfoot>
            </table>

            <div style={{
                marginTop: 16, padding: 12, background: '#f7f7f7', borderRadius: 4,
                fontSize: 13, color: '#444',
            }}>
                <div style={{ marginBottom: 6 }}>
                    <span style={{ color: '#666' }}>Modpos Kart Toplamı:</span>{' '}
                    <strong style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatTl(modposKart)} TL
                    </strong>
                </div>
                <div style={{ color: '#666', fontSize: 12 }}>
                    Beyan vs Z farkı &gt; 0.50 veya Beyan vs Modpos farkı &gt; 5.00 ise dikkat.
                </div>
                {(uyariBeyanZ || uyariBeyanModpos) && (
                    <div style={{
                        marginTop: 8, padding: 8, background: '#fff3cd', color: '#856404',
                        borderRadius: 4, fontSize: 12,
                    }}>
                        {uyariBeyanZ && (
                            <div>
                                Toplam Beyan ({formatTl(toplamBeyan)}) − Toplam Z ({formatTl(toplamZ)})
                                = {formatTl(toplamBeyan - toplamZ)} TL — tolerans dışı.
                            </div>
                        )}
                        {uyariBeyanModpos && (
                            <div>
                                Toplam Beyan ({formatTl(toplamBeyan)}) − Modpos ({formatTl(modposKart)})
                                = {formatTl(toplamBeyan - modposKart)} TL — tolerans dışı.
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Sekme 3: Yemek Kartı (G14)
// ─────────────────────────────────────────────────────────────────────
function YkSekme({ ykDetaylar, updateYk, ykSaglayicilari, karsilastirmaYk, readOnly }) {
    // Tanımsız ödeme tipleri: Modpos verisi var ama YK sağlayıcı master'ında yok
    const tanimsizlar = Object.entries(karsilastirmaYk).filter(([kod]) =>
        !ykSaglayicilari.find((s) => s.kod === kod)
    );

    return (
        <div>
            <h4 style={{ margin: '0 0 16px 0', fontSize: 15, fontWeight: 700, color: '#333' }}>
                Yemek Kartı — Sağlayıcılar
            </h4>

            {ykDetaylar.length === 0 ? (
                <div style={{
                    padding: 16, background: '#f7f7f7', borderRadius: 4, color: '#666',
                    fontSize: 13,
                }}>
                    Bu kapanış için Modpos'tan yemek kartı verisi gelmedi ve manuel sağlayıcı eklenmemiş.
                </div>
            ) : (
                <table style={tableStyle}>
                    <thead>
                        <tr>
                            <th style={thStyle}>Sağlayıcı</th>
                            <th style={thStyle}>Beyan</th>
                            <th style={thStyle}>Modpos</th>
                            <th style={thStyle}>Fark</th>
                        </tr>
                    </thead>
                    <tbody>
                        {ykDetaylar.map((d, idx) => {
                            const fark = (Number(d.beyan_tutar) || 0) - (Number(d.modpos_snapshot) || 0);
                            return (
                                <tr key={`${d.odeme_komisyon_id}_${idx}`}>
                                    <td style={tdStyle}>
                                        {d.saglayici_ad}
                                        {d._orphan && (
                                            <span style={{
                                                marginLeft: 6, fontSize: 10, color: '#856404',
                                                background: '#fff3cd', padding: '1px 6px', borderRadius: 8,
                                            }}>
                                                pasif
                                            </span>
                                        )}
                                    </td>
                                    <td style={tdStyle}>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={d.beyan_tutar}
                                            onChange={(e) => updateYk(idx, 'beyan_tutar', e.target.value)}
                                            disabled={readOnly}
                                            style={numInputStyle}
                                        />
                                    </td>
                                    <td style={{ ...tdStyle, fontVariantNumeric: 'tabular-nums', color: '#555' }}>
                                        {formatTl(d.modpos_snapshot)}
                                    </td>
                                    <td style={{
                                        ...tdStyle,
                                        fontVariantNumeric: 'tabular-nums',
                                        fontWeight: 600,
                                        color: farkColor(fark),
                                    }}>
                                        {fark > 0 ? '+' : ''}{formatTl(fark)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}

            {tanimsizlar.length > 0 && (
                <div style={{
                    marginTop: 16, padding: 12, background: '#fff3cd', color: '#856404',
                    borderRadius: 4, fontSize: 13,
                }}>
                    <strong>Tanımsız ödeme tipi(leri):</strong>
                    <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                        {tanimsizlar.map(([kod, tutar]) => (
                            <li key={kod} style={{ fontVariantNumeric: 'tabular-nums' }}>
                                {kod} (toplam {formatTl(tutar)} TL)
                            </li>
                        ))}
                    </ul>
                    <div style={{ marginTop: 6, fontSize: 12 }}>
                        Bu ödeme tiplerini Ayarlar &rarr; Komisyon Oranları'na eklemeniz önerilir.
                    </div>
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Sekme 4: Harcamalar (G15)
// ─────────────────────────────────────────────────────────────────────
function HarcamaSekme({ harcamalar, updateHarcama, harcamaSil, harcamaEkle, giderKategorileri, readOnly }) {
    const toplam = harcamalar.reduce((s, h) => s + (Number(h.tutar) || 0), 0);

    return (
        <div>
            <h4 style={{ margin: '0 0 8px 0', fontSize: 15, fontWeight: 700, color: '#333' }}>
                Harcamalar
            </h4>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 16 }}>
                Tamamlandığında her satır için otomatik Gider kaydı oluşturulur.
            </div>

            {harcamalar.length === 0 ? (
                <div style={{
                    padding: 16, background: '#f7f7f7', borderRadius: 4, color: '#666',
                    fontSize: 13, marginBottom: 12,
                }}>
                    Henüz harcama eklenmemiş.
                </div>
            ) : (
                <table style={tableStyle}>
                    <thead>
                        <tr>
                            <th style={{ ...thStyle, width: 220 }}>Gider Kategorisi</th>
                            <th style={thStyle}>Açıklama</th>
                            <th style={{ ...thStyle, width: 140 }}>Tutar</th>
                            <th style={{ ...thStyle, width: 50 }} />
                        </tr>
                    </thead>
                    <tbody>
                        {harcamalar.map((h, idx) => (
                            <tr key={idx}>
                                <td style={tdStyle}>
                                    <select
                                        value={h.gider_kategorisi}
                                        onChange={(e) => updateHarcama(idx, 'gider_kategorisi', e.target.value)}
                                        disabled={readOnly}
                                        style={{ ...inputStyle, width: '100%' }}
                                    >
                                        <option value="">Seçin...</option>
                                        {giderKategorileri.map((k) => (
                                            <option key={k.kod} value={k.kod}>{k.ad}</option>
                                        ))}
                                    </select>
                                </td>
                                <td style={tdStyle}>
                                    <input
                                        type="text"
                                        value={h.aciklama}
                                        onChange={(e) => updateHarcama(idx, 'aciklama', e.target.value)}
                                        disabled={readOnly}
                                        style={{ ...inputStyle, width: '100%' }}
                                        placeholder="Açıklama..."
                                    />
                                </td>
                                <td style={tdStyle}>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={h.tutar}
                                        onChange={(e) => updateHarcama(idx, 'tutar', e.target.value)}
                                        disabled={readOnly}
                                        style={numInputStyle}
                                    />
                                </td>
                                <td style={tdStyle}>
                                    {!readOnly && (
                                        <button
                                            type="button"
                                            onClick={() => harcamaSil(idx)}
                                            style={{
                                                background: 'transparent',
                                                border: '1px solid #c62828',
                                                color: '#c62828',
                                                borderRadius: 4,
                                                width: 28,
                                                height: 28,
                                                cursor: 'pointer',
                                                fontSize: 16,
                                                lineHeight: 1,
                                                fontFamily: 'inherit',
                                            }}
                                            title="Sil"
                                        >
                                            ×
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {!readOnly && (
                <button
                    type="button"
                    onClick={harcamaEkle}
                    style={{
                        marginTop: 12,
                        padding: '6px 14px',
                        background: '#fff',
                        color: '#c62828',
                        border: '1px dashed #c62828',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 13,
                        fontWeight: 600,
                        fontFamily: 'inherit',
                    }}
                >
                    + Harcama Ekle
                </button>
            )}

            <div style={{
                marginTop: 16, padding: 10, background: '#f7f7f7', borderRadius: 4,
                fontSize: 13, color: '#444',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
                <span>Toplam harcama</span>
                <strong style={{ fontVariantNumeric: 'tabular-nums', fontSize: 14 }}>
                    {formatTl(toplam)} TL
                </strong>
            </div>
        </div>
    );
}

function YeniKapanisModal({ lokasyonlar, onClose, onCreated }) {
    const [lokasyonId, setLokasyonId] = useState('');
    const [tarih, setTarih] = useState(todayIso());
    const [submitting, setSubmitting] = useState(false);
    const [hata, setHata] = useState('');

    const handleOlustur = async () => {
        if (!lokasyonId) {
            setHata('Lokasyon seçin');
            return;
        }
        if (!tarih) {
            setHata('Tarih seçin');
            return;
        }
        setHata('');
        setSubmitting(true);
        try {
            const r = await fetch(`${API_URL}/api/kasa-kapanis`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeaders(),
                },
                body: JSON.stringify({
                    lokasyon_id: Number(lokasyonId),
                    kapanis_tarihi: tarih,
                }),
            });
            const data = await r.json().catch(() => ({}));
            if (r.ok) {
                onCreated(data);
            } else {
                setHata(data.detail || `Oluşturma hatası (${r.status})`);
            }
        } catch (e) {
            setHata(`Ağ hatası: ${e.message || e}`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000,
        }}>
            <div style={{
                background: '#fff',
                borderRadius: 8,
                padding: 24,
                minWidth: 360,
                maxWidth: 480,
                boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
            }}>
                <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 16, fontWeight: 700, color: '#222' }}>
                    Yeni Kasa Kapanış
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <label>
                        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#444' }}>
                            Lokasyon
                        </div>
                        <select
                            value={lokasyonId}
                            onChange={(e) => setLokasyonId(e.target.value)}
                            style={{ ...inputStyle, width: '100%', padding: 8 }}
                            disabled={submitting}
                        >
                            <option value="">Seçin...</option>
                            {lokasyonlar.map((l) => (
                                <option key={l.id} value={l.id}>{l.ad}</option>
                            ))}
                        </select>
                    </label>

                    <label>
                        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#444' }}>
                            Kapanış Tarihi
                        </div>
                        <input
                            type="date"
                            value={tarih}
                            onChange={(e) => setTarih(e.target.value)}
                            style={{ ...inputStyle, width: '100%', padding: 8, boxSizing: 'border-box' }}
                            disabled={submitting}
                        />
                    </label>

                    {hata && (
                        <div style={{ color: '#c62828', fontSize: 13 }}>{hata}</div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={submitting}
                            style={{
                                padding: '8px 16px',
                                background: '#eee',
                                color: '#333',
                                border: 'none',
                                borderRadius: 4,
                                cursor: submitting ? 'wait' : 'pointer',
                                fontSize: 13,
                                fontFamily: 'inherit',
                                opacity: submitting ? 0.6 : 1,
                            }}
                        >
                            İptal
                        </button>
                        <button
                            type="button"
                            onClick={handleOlustur}
                            disabled={submitting || !lokasyonId}
                            style={{
                                ...primaryButtonStyle,
                                padding: '8px 16px',
                                opacity: submitting || !lokasyonId ? 0.6 : 1,
                                cursor: submitting || !lokasyonId ? 'not-allowed' : 'pointer',
                            }}
                        >
                            {submitting ? 'Oluşturuluyor...' : 'Oluştur'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────
// G16 — Sticky Üst Özet Bar
// ─────────────────────────────────────────────────────────────────────
// Sağ panelin en üstünde; sticky → kullanıcı sekme içinde scroll ederken
// toplamlar hep görünür. Working state değiştikçe canlı yenilenir
// (useMemo, parent'tan ozet prop'u).
//
// Sign convention (fark):
//   + (yeşil/kırmızı'a göre) → beyan > kaynak (eldeki para fazla)
//   −                      → beyan < kaynak (eksik var)
// Renk: |fark| <= tolerans → yeşil; ötesi → kırmızı.
function OzetBar({ ozet, tolerans }) {
    const [scrolled, setScrolled] = useState(false);
    const ref = useRef(null);

    // Kullanıcı içeride scroll ederken hafif gölge ekle — sticky'nin "yüzdüğü" hissi
    useEffect(() => {
        const handler = () => setScrolled(window.scrollY > 4 || (ref.current && ref.current.parentElement && ref.current.parentElement.scrollTop > 4));
        // sağ panel kendi içinde scroll ediyor; nearest scroller'ı yakala
        const scroller = ref.current?.closest('section');
        if (!scroller) return undefined;
        scroller.addEventListener('scroll', handler, { passive: true });
        return () => scroller.removeEventListener('scroll', handler);
    }, []);

    const farkRenk = ozet.tolerasInde ? '#2e7d32' : '#c62828';
    const farkBg = ozet.tolerasInde ? '#e8f5e9' : '#ffebee';
    const farkIcon = ozet.tolerasInde ? 'OK' : '!';
    const farkSign = ozet.toplamFark > 0 ? '+' : ozet.toplamFark < 0 ? '−' : '';
    const farkAbs = Math.abs(ozet.toplamFark);

    return (
        <div
            ref={ref}
            style={{
                position: 'sticky',
                top: 0,
                zIndex: 40,
                background: '#fff',
                borderBottom: '1px solid #e0e0e0',
                padding: '10px 14px',
                marginTop: -24,
                marginLeft: -24,
                marginRight: -24,
                boxShadow: scrolled ? '0 2px 4px rgba(0,0,0,0.06)' : 'none',
                transition: 'box-shadow 120ms ease',
            }}
        >
            <div style={{
                display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 18,
                fontSize: 13, color: '#333', fontVariantNumeric: 'tabular-nums',
            }}>
                <OzetCell label="Nakit" value={ozet.nakit} />
                <OzetCell label="Kart" value={ozet.kart} />
                <OzetCell label="YK" value={ozet.yk} />
                <OzetCell label="Harcama" value={-ozet.harcama} signed />
                <span style={{ color: '#ddd' }}>│</span>
                <span>
                    <span style={{ color: '#666' }}>Net:</span>{' '}
                    <strong style={{ fontSize: 14, color: '#222' }}>{formatTl(ozet.net)} TL</strong>
                </span>
                <span style={{ marginLeft: 'auto' }} />
                <span style={{
                    background: farkBg, color: farkRenk,
                    padding: '4px 10px', borderRadius: 14,
                    fontSize: 13, fontWeight: 700,
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                }}>
                    <span style={{
                        fontSize: 10, fontWeight: 800,
                        width: 16, height: 16, borderRadius: '50%',
                        background: farkRenk, color: '#fff',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        {farkIcon}
                    </span>
                    <span>
                        Fark: {farkSign}{formatTl(farkAbs)} TL
                    </span>
                    <span style={{ fontWeight: 500, fontSize: 11, color: '#666', marginLeft: 4 }}>
                        (tolerans: {formatTl(tolerans)} TL)
                    </span>
                </span>
            </div>
        </div>
    );
}

function OzetCell({ label, value, signed }) {
    const sign = signed && value < 0 ? '−' : signed && value > 0 ? '+' : '';
    const display = Math.abs(value);
    return (
        <span>
            <span style={{ color: '#666' }}>{label}:</span>{' '}
            <strong style={{ color: '#222' }}>
                {sign}{formatTl(display)} TL
            </strong>
        </span>
    );
}

// ─────────────────────────────────────────────────────────────────────
// G16 — Tamamla Modal
// ─────────────────────────────────────────────────────────────────────
// İki davranış:
//   - Tolerans içinde: tek tık ile tamamla, açıklama gerekmez.
//   - Tolerans dışı: kırmızı uyarı + zorunlu açıklama textarea + "Açıklama ile Tamamla".
//
// PATCH /api/kasa-kapanis/{id}/tamamla
//   body: { fark_aciklamasi: text } (tolerans dışı) ya da {} (içinde)
// Backend G8 endpoint'i atomic: header güncelle + Gider INSERT + audit (tolerans
// dışı ise). 200 → güncel kapanış dönüyor, parent state'i refresh.
function TamamlaModal({ kapanisId, ozet, tolerans, onClose, onCompleted }) {
    const [aciklama, setAciklama] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [hata, setHata] = useState('');

    const toleransDisi = !ozet.tolerasInde;
    const aciklamaGecerli = aciklama.trim().length > 0;

    const handleSubmit = async () => {
        if (toleransDisi && !aciklamaGecerli) {
            setHata('Tolerans dışı kapanışlar için açıklama zorunludur.');
            return;
        }
        setHata('');
        setSubmitting(true);
        try {
            const body = toleransDisi ? { fark_aciklamasi: aciklama.trim() } : {};
            const r = await fetch(`${API_URL}/api/kasa-kapanis/${kapanisId}/tamamla`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify(body),
            });
            const data = await r.json().catch(() => ({}));
            if (r.ok) {
                onCompleted(data);
            } else {
                setHata(data.detail || `Tamamlama hatası (${r.status})`);
            }
        } catch (e) {
            setHata(`Ağ hatası: ${e.message || e}`);
        } finally {
            setSubmitting(false);
        }
    };

    const farkSign = ozet.toplamFark > 0 ? '+' : ozet.toplamFark < 0 ? '−' : '';
    const farkAbs = Math.abs(ozet.toplamFark);

    return (
        <div style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1100,
        }}>
            <div style={{
                background: '#fff', borderRadius: 8, padding: 24,
                minWidth: 420, maxWidth: 540,
                boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
            }}>
                <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 16, fontWeight: 700, color: '#222' }}>
                    Kasa Kapanışı Tamamla
                </h3>

                <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8,
                    padding: 12, background: '#fafafa', borderRadius: 4,
                    fontSize: 13, marginBottom: 14, fontVariantNumeric: 'tabular-nums',
                }}>
                    <div><span style={{ color: '#666' }}>Net:</span> <strong>{formatTl(ozet.net)} TL</strong></div>
                    <div><span style={{ color: '#666' }}>Tolerans:</span> <strong>{formatTl(tolerans)} TL</strong></div>
                    <div style={{ gridColumn: '1 / -1' }}>
                        <span style={{ color: '#666' }}>Toplam fark:</span>{' '}
                        <strong style={{ color: toleransDisi ? '#c62828' : '#2e7d32' }}>
                            {farkSign}{formatTl(farkAbs)} TL
                        </strong>
                    </div>
                </div>

                {toleransDisi ? (
                    <>
                        <div style={{
                            background: '#ffebee', color: '#c62828',
                            padding: 10, borderRadius: 4, fontSize: 13, marginBottom: 12,
                        }}>
                            Toplam fark {farkSign}{formatTl(farkAbs)} TL — tolerans ({formatTl(tolerans)} TL) aşıldı.
                            Tamamlamak için açıklama zorunlu.
                        </div>
                        <label>
                            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#444' }}>
                                Fark açıklaması (zorunlu)
                            </div>
                            <textarea
                                value={aciklama}
                                onChange={(e) => setAciklama(e.target.value)}
                                disabled={submitting}
                                rows={4}
                                style={{
                                    ...inputStyle, width: '100%', boxSizing: 'border-box',
                                    fontFamily: 'inherit', padding: 10, resize: 'vertical',
                                }}
                                placeholder="Farkın sebebi (ör. 50 TL nakit eksik — bordro avansı, kayıt yarın yapılacak)..."
                            />
                        </label>
                    </>
                ) : (
                    <div style={{
                        background: '#e8f5e9', color: '#1b5e20',
                        padding: 10, borderRadius: 4, fontSize: 13, marginBottom: 12,
                    }}>
                        Fark tolerans içinde — doğrudan tamamlanabilir.
                    </div>
                )}

                {hata && (
                    <div style={{ color: '#c62828', fontSize: 13, marginTop: 10 }}>{hata}</div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={submitting}
                        style={{
                            padding: '8px 16px',
                            background: '#eee', color: '#333',
                            border: 'none', borderRadius: 4,
                            cursor: submitting ? 'wait' : 'pointer',
                            fontSize: 13, fontFamily: 'inherit',
                            opacity: submitting ? 0.6 : 1,
                        }}
                    >
                        İptal
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={submitting || (toleransDisi && !aciklamaGecerli)}
                        style={{
                            padding: '8px 18px',
                            background: '#2e7d32', color: '#fff',
                            border: 'none', borderRadius: 4,
                            cursor: submitting ? 'wait' : 'pointer',
                            fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
                            opacity: (submitting || (toleransDisi && !aciklamaGecerli)) ? 0.5 : 1,
                        }}
                    >
                        {submitting
                            ? 'Tamamlanıyor...'
                            : (toleransDisi ? 'Açıklama ile Tamamla' : 'Tamamla')}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────
// G16 — Yeniden Aç Modal
// ─────────────────────────────────────────────────────────────────────
// TAMAMLANDI durumdaki kapanışı tekrar TASLAK'a alır. Açıklama zorunlu
// (backend YENIDEN_AC audit kaydına yazılır). Gider INSERT'leri silinmez —
// G8 idempotent: yeniden tamamla sırasında harcama.gider_id korunduğu için
// duplicate Gider üretilmez.
//
// Yetki: YENIDEN_AC_KASA_KAPANIS (yonetim/admin).
function YenidenAcModal({ kapanisId, onClose, onReopened }) {
    const [aciklama, setAciklama] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [hata, setHata] = useState('');

    const handleSubmit = async () => {
        const txt = aciklama.trim();
        if (!txt) {
            setHata('Yeniden açma sebebi zorunludur.');
            return;
        }
        setHata('');
        setSubmitting(true);
        try {
            const r = await fetch(`${API_URL}/api/kasa-kapanis/${kapanisId}/yeniden-ac`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ aciklama: txt }),
            });
            const data = await r.json().catch(() => ({}));
            if (r.ok) {
                onReopened(data);
            } else {
                setHata(data.detail || `Yeniden açma hatası (${r.status})`);
            }
        } catch (e) {
            setHata(`Ağ hatası: ${e.message || e}`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1100,
        }}>
            <div style={{
                background: '#fff', borderRadius: 8, padding: 24,
                minWidth: 380, maxWidth: 480,
                boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
            }}>
                <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: 16, fontWeight: 700, color: '#222' }}>
                    Kapanışı Yeniden Aç
                </h3>
                <div style={{
                    fontSize: 13, color: '#555', marginBottom: 14, lineHeight: 1.5,
                }}>
                    Tamamlanmış kapanış TASLAK durumuna alınacak. Daha önce oluşturulan
                    Gider kayıtları silinmez; yeniden tamamlandığında duplicate üretilmez.
                </div>

                <label>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#444' }}>
                        Yeniden açma sebebi (zorunlu)
                    </div>
                    <textarea
                        value={aciklama}
                        onChange={(e) => setAciklama(e.target.value)}
                        disabled={submitting}
                        rows={4}
                        style={{
                            ...inputStyle, width: '100%', boxSizing: 'border-box',
                            fontFamily: 'inherit', padding: 10, resize: 'vertical',
                        }}
                        placeholder="Ör. Z raporu sehven yanlış girilmiş, düzeltilecek..."
                    />
                </label>

                {hata && (
                    <div style={{ color: '#c62828', fontSize: 13, marginTop: 10 }}>{hata}</div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={submitting}
                        style={{
                            padding: '8px 16px',
                            background: '#eee', color: '#333',
                            border: 'none', borderRadius: 4,
                            cursor: submitting ? 'wait' : 'pointer',
                            fontSize: 13, fontFamily: 'inherit',
                            opacity: submitting ? 0.6 : 1,
                        }}
                    >
                        İptal
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={submitting || !aciklama.trim()}
                        style={{
                            ...primaryButtonStyle,
                            padding: '8px 18px',
                            opacity: (submitting || !aciklama.trim()) ? 0.5 : 1,
                            cursor: submitting ? 'wait' : 'pointer',
                        }}
                    >
                        {submitting ? 'Yeniden açılıyor...' : 'Yeniden Aç'}
                    </button>
                </div>
            </div>
        </div>
    );
}
